'use client';

import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@koda/koda-components/atoms/Button';
import { Card } from '@koda/koda-components/atoms/Card';
import ToolTip from '@koda/koda-components/components/molecules/ToolTip';
import { formatCurrency } from '@koda/koda-utils/formatMoney';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { DA, EN, type MainLocale } from '../../../../../../i18n/constants';
import { BUSINESS_INFORMATION_ROUTE } from '../../../../../../i18n/pathnames/licenses';
import { FlowController } from '../../FlowController';
import { useLicenseStore } from '../../licenseStore';
import type { SanityLicense } from '../../types/licenses';
import { BusinessInformation } from '../business-information/forms/types/businessInformation';

type Props = {
  license?: SanityLicense;
  inBasketPanel?: boolean;
  inReceiptPage?: boolean;
};

type MaybeHasLiveMusic = { hasLiveMusic?: unknown };

const isTruthyLiveMusic = (value: unknown): boolean => value === true;

const extractHasLiveMusic = (form: BusinessInformation | undefined): boolean => {
  if (!form) return false;
  const candidate = form as MaybeHasLiveMusic;
  return isTruthyLiveMusic(candidate.hasLiveMusic);
};

export default function BillingDetails({ license, inBasketPanel, inReceiptPage }: Props) {
  const locale = useLocale();
  const router = useRouter();

  const { licensePlan, countryCode } = useLicenseStore(s => ({
    licensePlan: s.licensePlan,
    countryCode: s.customerInformation?.venue?.countryRegionCode,
  }));

  const priceWithoutVat = countryCode && countryCode !== 'DK';

  const hasLiveMusic = useLicenseStore(s => extractHasLiveMusic(s.businessInformation));

  const t = useTranslations('licenses.billingDetails');
  const tBasket = useTranslations('licenses.basketPanel.basket');

  if (!licensePlan) return null;

  const goToEdit = () => {
    if (!license) return null;
    const flowController = new FlowController(license);
    const mainLocale: MainLocale = locale === EN ? EN : DA;
    const editUrl = flowController.getAbsoluteRouteForStep(BUSINESS_INFORMATION_ROUTE, mainLocale);

    if (editUrl) {
      router.push(editUrl);
    }
  };

  const fmt = (amount?: number) => (typeof amount === 'number' ? formatCurrency(amount, locale) : '—');

  const isSubscription = licensePlan.recurringPeriodPriceInclTax !== 0;

  const hasPriceBreakdown =
    typeof licensePlan.classesPriceExclTax === 'number' && typeof licensePlan.arrangementPriceExclTax === 'number';

  const priceRate = () => {
    let translation;
    let price;

    if (!isSubscription) {
      return null;
    }

    if (licensePlan.recurringPeriodTimeFactor === 'Quarterly') {
      translation = t('quarterlyPrice');
      price = fmt(licensePlan.recurringPeriodPriceInclTax * 3);
    } else if (licensePlan.recurringPeriodTimeFactor === 'Biannual') {
      translation = t('HalfYearPrice');
      price = fmt(licensePlan.recurringPeriodPriceInclTax * 6);
    } else return null;

    return (
      <div>
        <div className="flex items-center justify-between text-md">
          <dt className="font-normal">{translation}</dt>
          <dd className="font-semibold">{price}</dd>
        </div>
        {!inBasketPanel && (
          <div className="w-full text-right">
            <p className="text-md font-normal">{`${t('monthlyPrice')} ${fmt(licensePlan.priceInclTax)}`}</p>
          </div>
        )}
      </div>
    );
  };

  const textId = inBasketPanel ? tBasket('musicLicense') : inReceiptPage ? t('musicLicenseOverview') : t('header');
  const subscriptionTypeContent = () => {
    switch (licensePlan.recurringPeriodTimeFactor) {
      case 'Biannual':
        return t('billingFrequencyHalfYearly');
      case 'Quarterly':
        return t('billingFrequencyQuarterly');
      default:
        return null;
    }
  };

  return (
    <Card
      flex={false}
      backgroundColor={'white'}
      hue={500}
      rounded={'2xl'}
      className={inBasketPanel ? 'hidden p-8 md:block' : 'px-6 py-8 md:p-8'}
    >
      <section aria-labelledby={textId}>
        <div className="flex w-full flex-col gap-6 font-graphik">
          <div className="flex items-center justify-between">
            <h2 id={textId} className="text-lg font-medium md:text-xl">
              {textId}
            </h2>
            {inBasketPanel && (
              <Button onClick={goToEdit} buttonType={'link'} variant={'inlineLink'}>
                {tBasket('edit')}
              </Button>
            )}
          </div>
          {(inBasketPanel || inReceiptPage) && (
            <div className="col-span-8">
              <dl className="space-y-3">
                {(inBasketPanel || inReceiptPage) && (
                  <div className="flex items-center justify-between text-md">
                    <dt className="font-normal">{tBasket('type')}</dt>
                    <dd className="font-medium" title={license?.name ?? undefined}>
                      {license?.name}
                    </dd>
                  </div>
                )}
                {isSubscription && (
                  <>
                    {priceRate()}
                    {licensePlan.recurringPeriodPriceInclTax && (
                      <div className="flex items-center justify-between text-md">
                        <dt className="inline-block text-md font-normal">
                          {t('discount')}
                          <ToolTip
                            content={
                              <div className="flex flex-col gap-2 font-graphik">
                                <div className="text-lg font-semibold">{t('discountInfoTitle')}</div>
                                <div className="text-sm font-normal">{t('discountInfoDescription')}</div>
                              </div>
                            }
                          >
                            <InformationCircleIcon className="ml-1 inline-block size-5" aria-hidden="true" />
                          </ToolTip>
                        </dt>
                      </div>
                    )}
                    {inReceiptPage && (
                      <div>
                        <div className="flex items-center justify-between text-md">
                          <dt className="font-normal">{t('billingFrequencyLabel')}</dt>
                          <dd className="font-semibold">{subscriptionTypeContent()}</dd>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </dl>
            </div>
          )}
          <hr className="border-black-100" />
          {hasLiveMusic && (
            <div className="col-span-8 space-y-1 rounded-md bg-lightblue-50 p-4">
              <div className="flex justify-between">
                <h3 className="text-lg font-medium">{tBasket('liveMusic.title')}</h3>
                <Button variant="inlineLink" buttonType="button" onClick={goToEdit}>
                  {tBasket('liveMusic.edit')}
                </Button>
              </div>
              <p className="text-md font-normal">{tBasket('liveMusic.description')}</p>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {hasPriceBreakdown && (
              <dl className="space-y-1">
                <div className="flex items-center justify-between text-md">
                  <dt className="font-normal">{t('classesPrice')}</dt>
                  <dd className="font-normal">{fmt(licensePlan.classesPriceExclTax)}</dd>
                </div>
                <div className="flex items-center justify-between text-md">
                  <dt className="font-normal">{t('arrangementPrice')}</dt>
                  <dd className="font-normal">{fmt(licensePlan.arrangementPriceExclTax)}</dd>
                </div>
              </dl>
            )}
            <div className="flex flex-col gap-1">
              <div className="flex items-end justify-between">
                {isSubscription ? (
                  <div className="flex flex-col">
                    <span className="text-xs font-normal">{tBasket('firstPaymentOverline')}</span>
                    <p className="text-md font-medium">{tBasket('firstPayment')}</p>
                  </div>
                ) : (
                  <p className="text-md font-medium">{tBasket('totalWithoutVat')}</p>
                )}
                <p className="text-lg font-medium">
                  {!isSubscription && licensePlan.priceExclTax && fmt(licensePlan.priceExclTax)}
                  {isSubscription &&
                    licensePlan.recurringPeriodPriceInclTax &&
                    fmt(licensePlan.recurringPeriodPriceInclTax)}
                </p>
              </div>
              {!priceWithoutVat && (
                <div className="flex justify-between">
                  <p className="text-md font-normal">{tBasket('totalWithVat')}</p>
                  <p className="text-md font-normal">
                    {!isSubscription && licensePlan.priceInclTax && fmt(licensePlan.priceInclTax)}
                    {isSubscription &&
                      licensePlan.recurringPeriodPriceInclTax &&
                      fmt(licensePlan.recurringPeriodPriceInclTax)}
                  </p>
                </div>
              )}
            </div>
            {!inBasketPanel && !inReceiptPage && <p className="text-md font-normal">{subscriptionTypeContent()}</p>}
          </div>
        </div>
      </section>
    </Card>
  );
}
